var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import Tooltip from '@mui/material/Tooltip';
import debounce from 'lodash/debounce';
import { FormatMoney } from '@components/Shared/Utils/price';
import clsx from 'clsx';
import { produce } from 'immer';
import CircularProgress from '@mui/material/CircularProgress';
import ShopifyAjaxApi from '@themes/common/shopifyApi';
import { Distinct } from '@commons/helpers';
import { IsMobile } from '@themes/common/utils';
export const RemToPx = (rem) => rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
const CardPriceDisplay = ({ priceWrapperElement, productData, loading, isShopify }) => {
    var _a, _b, _c, _d, _e, _f;
    const discounted = ((_a = productData === null || productData === void 0 ? void 0 : productData.compare_at_price) !== null && _a !== void 0 ? _a : 0) > ((_b = productData === null || productData === void 0 ? void 0 : productData.price) !== null && _b !== void 0 ? _b : 0);
    useEffect(() => {
        if (productData)
            priceWrapperElement.addClass('price-variant');
    }, [priceWrapperElement, productData]);
    return createPortal(_jsxs(_Fragment, { children: [!!productData &&
                (isShopify ? (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: clsx('price__current price-variant', {
                                'price__current--on-sale': discounted,
                            }) }, { children: [FormatMoney((_c = productData === null || productData === void 0 ? void 0 : productData.unit_price) !== null && _c !== void 0 ? _c : 0), _jsxs("span", Object.assign({ className: 'sb-units' }, { children: [" / ", productData === null || productData === void 0 ? void 0 : productData.unit] }))] })), discounted && (_jsxs("div", Object.assign({ className: 'price__compare-at visible price-variant' }, { children: [FormatMoney((_d = productData === null || productData === void 0 ? void 0 : productData.unit_compare_at_price) !== null && _d !== void 0 ? _d : 0), _jsxs("span", Object.assign({ className: 'sb-units' }, { children: [" / ", productData === null || productData === void 0 ? void 0 : productData.unit] }))] })))] })) : (_jsxs(_Fragment, { children: [_jsx("p", Object.assign({ className: 'ais-hit--price-striked price-variant' }, { children: _jsx("span", { children: _jsx("span", Object.assign({ className: clsx({ striked: discounted }) }, { children: discounted && (_jsxs(_Fragment, { children: [FormatMoney((_e = productData === null || productData === void 0 ? void 0 : productData.unit_compare_at_price) !== null && _e !== void 0 ? _e : 0), _jsxs("small", Object.assign({ className: 'sb-units' }, { children: ["/ ", productData === null || productData === void 0 ? void 0 : productData.unit] }))] })) })) }) })), _jsx("p", Object.assign({ className: 'ais-hit--price price-variant' }, { children: _jsx("b", { children: _jsxs("span", Object.assign({ className: clsx({ discounted }) }, { children: [FormatMoney((_f = productData === null || productData === void 0 ? void 0 : productData.unit_price) !== null && _f !== void 0 ? _f : 0), _jsxs("small", Object.assign({ className: 'sb-units' }, { children: ["/ ", productData === null || productData === void 0 ? void 0 : productData.unit] }))] })) }) }))] }))), loading && (_jsx(CircularProgress, { size: 30, style: { color: 'var(--primary-color)' }, className: 'price-variant absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' }))] }), priceWrapperElement[0]);
};
const HoverVariantImage = ({ variant, containerElement }) => {
    return createPortal(_jsx("div", { className: clsx('absolute inset-0 image-cover cover-loaded', {
            // hidden: !variant,
            'z-20': variant,
        }), style: {
            backgroundImage: variant ? `url(${variant.product_image_url})` : undefined,
        } }), containerElement[0]);
};
const Variations = ({ handle, element, productCardElement, productCardData, containerElement, priceWrapperElement, isShopify }) => {
    const variantWrappers = useRef(null);
    const [displayedElements, setDisplayedElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [variants, setVariants] = useState([]);
    const [hoveredVariant, setHoveredVariant] = useState(null);
    const [defaultSelected, setDefaultSelected] = useState(null);
    const [productsData, setProductsData] = useState({});
    const [lastViewedVariant, setLastViewedVariant] = useState(null);
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const response = yield ShopifyAjaxApi.GetProductByHandle(handle, {
                view: 'sb-product-options-json',
            });
            const allOptions = (_b = (_a = response === null || response === void 0 ? void 0 : response.variant_options) === null || _a === void 0 ? void 0 : _a.flatMap((variant) => variant.options.filter((option) => option.toLowerCase() !== 'default title'))) !== null && _b !== void 0 ? _b : [];
            if (allOptions.length > 1) {
                const variantOptionInfo = (_c = response === null || response === void 0 ? void 0 : response.variant_options.map((variant) => {
                    var _a, _b;
                    const colorPattern = response === null || response === void 0 ? void 0 : response.color_patterns.find((color) => variant.options.includes(color.label));
                    const color = (_a = colorPattern === null || colorPattern === void 0 ? void 0 : colorPattern.color) !== null && _a !== void 0 ? _a : null;
                    const image = (_b = colorPattern === null || colorPattern === void 0 ? void 0 : colorPattern.image) !== null && _b !== void 0 ? _b : null;
                    return Object.assign(Object.assign({}, variant), { color,
                        image });
                })) !== null && _c !== void 0 ? _c : [];
                setVariants(variantOptionInfo);
                setDefaultSelected((_d = variantOptionInfo.find((variant) => variant.variant_id === (productCardData === null || productCardData === void 0 ? void 0 : productCardData.variantId))) !== null && _d !== void 0 ? _d : variantOptionInfo[0]);
                element.style.display = 'block';
                element.style.opacity = '1';
            }
            else {
                element.style.display = 'none';
                element.style.opacity = '0';
            }
        });
        const dataDebounceFetch = debounce(fetchData, 150);
        dataDebounceFetch();
        return () => {
            dataDebounceFetch.cancel();
        };
    }, [element, handle, productCardData === null || productCardData === void 0 ? void 0 : productCardData.variantId]);
    useEffect(() => {
        var _a;
        if (!hoveredVariant || displayedElements === 0 || productsData[(_a = hoveredVariant.variant_id) !== null && _a !== void 0 ? _a : ''] || IsMobile())
            return;
        const fetchDataByHandle = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            // preload other data
            const variantIds = Distinct(variants
                .filter((v) => { var _a; return Object.keys(productsData).indexOf((_a = v.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) === -1; })
                .map((v) => v.variant_id)
                .slice(0, displayedElements));
            const preloadHandlesSetPromise = variantIds.flatMap((variantId) => {
                const productHandle = variants.find((v) => v.variant_id === variantId).product_handle;
                return variantIds.map((variant) => __awaiter(void 0, void 0, void 0, function* () {
                    try {
                        const data = yield ShopifyAjaxApi.GetProductByHandle(productHandle, {
                            view: 'sb-json',
                            variant,
                        });
                        return data;
                    }
                    catch (_a) {
                        // do nothing
                        return null;
                    }
                }));
            });
            const products = (yield Promise.all(preloadHandlesSetPromise)).filter((data) => data !== null);
            setProductsData(produce((draft) => {
                products.forEach((data) => {
                    draft[data.variant_id] = data;
                });
            }));
            setLoading(false);
        });
        const dataDebounceFetch = debounce(fetchDataByHandle, 150);
        dataDebounceFetch();
        return () => {
            dataDebounceFetch.cancel();
            setLoading(false);
        };
    }, [hoveredVariant, productsData, defaultSelected, variants, displayedElements]);
    const productData = useMemo(() => {
        var _a, _b, _c;
        const variant = (_a = lastViewedVariant !== null && lastViewedVariant !== void 0 ? lastViewedVariant : hoveredVariant) !== null && _a !== void 0 ? _a : defaultSelected;
        const selectedProduct = (_c = productsData[(_b = variant === null || variant === void 0 ? void 0 : variant.variant_id) !== null && _b !== void 0 ? _b : '']) !== null && _c !== void 0 ? _c : Object.values(productsData)[0];
        return selectedProduct !== null && selectedProduct !== void 0 ? selectedProduct : null;
    }, [hoveredVariant, lastViewedVariant, productsData, defaultSelected]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (productData) {
            containerElement.addClass('price-variant');
            const lipscroreWrapper = productCardElement.find('.sb-lipscore');
            lipscroreWrapper.attr('data-ls-product-id', productData.sku).removeClass('loaded');
            const badges = (_a = globalThis.sbBadges) === null || _a === void 0 ? void 0 : _a.call(globalThis, productData, 'Product Card');
            const existingBadges = containerElement.find('.sb-meta-badge--wrapper');
            const positions = [
                'default',
                'top-left',
                'top-center',
                'top-right',
                'center-left',
                'center-center',
                'center-right',
                'bottom-left',
                'bottom-center',
                'bottom-right',
            ];
            const newBadges = jQuery(badges);
            for (const position of positions) {
                const sbMetaBadgeClass = `.sb-meta-badge--${position}`;
                const existingBadge = existingBadges.filter(sbMetaBadgeClass);
                const badge = newBadges.filter(sbMetaBadgeClass);
                badge.addClass('price-variant');
                if (position === 'bottom-left') {
                    existingBadge.addClass('price-variant');
                    continue;
                }
                if (existingBadge.length === 0 && badge.length > 0) {
                    containerElement.append(badge);
                }
                else if (existingBadge.length > 0 && badge.length > 0) {
                    existingBadge.replaceWith(badge);
                }
            }
            (_b = globalThis.initializeFitBlock) === null || _b === void 0 ? void 0 : _b.call(globalThis);
            (_c = globalThis.initializeLipscore) === null || _c === void 0 ? void 0 : _c.call(globalThis);
            const bottomLeftContainer = containerElement.find('.sb-meta-badge--bottom-left');
            const bottomRightContainer = containerElement.find('.sb-meta-badge--bottom-right');
            const pictureWidth = (_d = containerElement.width()) !== null && _d !== void 0 ? _d : 0;
            const bottomLeftWidth = (_e = bottomLeftContainer === null || bottomLeftContainer === void 0 ? void 0 : bottomLeftContainer.width()) !== null && _e !== void 0 ? _e : 0;
            const bottomRightWidth = (_f = bottomRightContainer === null || bottomRightContainer === void 0 ? void 0 : bottomRightContainer.width()) !== null && _f !== void 0 ? _f : 0;
            if (pictureWidth < bottomLeftWidth + bottomRightWidth) {
                bottomRightContainer === null || bottomRightContainer === void 0 ? void 0 : bottomRightContainer.css('margin-bottom', (bottomLeftContainer === null || bottomLeftContainer === void 0 ? void 0 : bottomLeftContainer.height()) + 'px');
            }
        }
    }, [productData, productCardElement, containerElement]);
    useEffect(() => {
        var _a, _b;
        const variant = hoveredVariant !== null && hoveredVariant !== void 0 ? hoveredVariant : defaultSelected;
        const selectedProduct = productsData[(_a = variant === null || variant === void 0 ? void 0 : variant.variant_id) !== null && _a !== void 0 ? _a : ''];
        if (variant && selectedProduct) {
            setLastViewedVariant(variant);
        }
        const wrapperWidth = (_b = containerElement.width()) !== null && _b !== void 0 ? _b : 0;
        const width = RemToPx(2) + 4;
        const totalDisplayElement = Math.floor(wrapperWidth / width);
        const totalHiddenElement = variants.length - totalDisplayElement;
        if (totalHiddenElement > 0) {
            setDisplayedElements(totalDisplayElement - 1);
        }
        else {
            setDisplayedElements(variants.length);
        }
    }, [hoveredVariant, productsData, defaultSelected, variants, productCardElement, containerElement]);
    const handleHoveredVariant = useCallback((hoveredVariant) => {
        setHoveredVariant(hoveredVariant);
    }, []);
    return variants.length > 1 ? (_jsxs("div", Object.assign({ ref: variantWrappers, className: 'flex gap-1 overflow-hidden m-1!', onMouseLeave: () => handleHoveredVariant(null) }, { children: [_jsx(CardPriceDisplay, { productData: productData, priceWrapperElement: priceWrapperElement, isShopify: isShopify, loading: loading }), _jsx(HoverVariantImage, { variant: hoveredVariant, containerElement: containerElement }), variants.map((variant, index) => {
                var _a;
                return (index < displayedElements || displayedElements == 0) && (_jsx(Tooltip, Object.assign({ title: variant.options.filter((option) => option.toLowerCase() !== 'default title').join(' ') }, { children: _jsx("div", { onMouseEnter: () => handleHoveredVariant(variant), onClick: (e) => {
                            e.preventDefault();
                            setDefaultSelected(variant.variant_id === (defaultSelected === null || defaultSelected === void 0 ? void 0 : defaultSelected.variant_id) ? null : variant);
                        }, className: 'p-2! rounded-full image-cover cover-loaded shrink-0 sb-variant--thumb-image', style: variant.image
                            ? {
                                backgroundImage: `url(${variant.image})`,
                            }
                            : variant.color
                                ? {
                                    backgroundColor: (_a = variant.color) !== null && _a !== void 0 ? _a : undefined,
                                }
                                : {
                                    backgroundImage: `url(${variant.product_image_url})`,
                                } }, variant.variant_id) }), variant.variant_id));
            }), displayedElements > 0 && variants.length > displayedElements && (_jsxs("div", Object.assign({ className: 'rounded-full image-cover cover-loaded shrink-0 sb-variant--thumb-image bg-white text-black flex justify-center items-center font-semibold text-xs' }, { children: ["+", variants.length - displayedElements] })))] }))) : (_jsx(_Fragment, {}));
};
export default function LoadDataVariations(element) {
    const handle = element.getAttribute('data-product-handle');
    let productCardElement = jQuery(element).closest('.ais-hit.ais-product');
    let containerElement = productCardElement.find('.ais-hit--picture');
    let priceWrapperElement = productCardElement.find('.ais-hit--price-wrapper');
    let isShopify = false;
    if (!productCardElement.length) {
        productCardElement = jQuery(element).closest('.product-item');
        containerElement = productCardElement.find('.product-item--image-link');
        priceWrapperElement = productCardElement.find('.price.product-item__price');
        isShopify = true;
    }
    const productCardData = productCardElement.data();
    const root = createRoot(element);
    root.render(_jsx(Variations, { handle: handle, element: element, productCardData: productCardData, productCardElement: productCardElement, containerElement: containerElement, priceWrapperElement: priceWrapperElement, isShopify: isShopify }));
}
